import React from 'react';
import { z } from 'zod';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { WidgetRegistry } from './WidgetRegistry';
import type { WidgetRenderProps } from './WidgetRegistry';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import type { ChartWidgetData } from '@trinity/chat/types/dashboard-data';
import type { ChartWidgetConfig, DashboardWidgetType } from '@trinity/chat/types/dashboard-config';
import { ChartWidgetConfigSchema, TextWidgetConfigSchema, MetricCardWidgetConfigSchema, CodeWidgetConfigSchema, RepDashboardWidgetConfigSchema, QuoteWidgetConfigSchema } from '@trinity/chat/types/dashboard-config';
import type { RootNode } from '@trinity/chat/src/types/rich-text';
import { ChartVisualizationUI } from '@trinity/chat-components/components/Widgets/ChartWidgetRouter';
import { KPIWidget } from '@trinity/chat-components/components/Widgets/KPIWidget';
import { RichTextUI } from '@trinity/chat-components/components/Widgets/RichTextWidget';
import { RepDashboardWidget } from '@trinity/chat-components/components/Widgets/RepDashboardWidget';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';
// Exception to the "no dynamic imports" rule in AGENTS.md: Sandpack is ~500KB
// minified and only needed when a page actually contains a code widget. Lazy-loading
// keeps it out of the main bundle so other pages are unaffected.
const CodeWidgetUI = React.lazy(() =>
  import('@trinity/chat-components/components/Widgets/CodeWidget').then(m => ({ default: m.CodeWidgetUI }))
);

// ─── Shared styled components ────────────────────────────────────────────────

const WidgetContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'auto',
  padding: '8px',
  boxSizing: 'border-box',
});

const Attribution = styled.div({
  fontSize: '11px',
  color: '#5C73AD',
  backgroundColor: '#f1f0f6',
  marginTop: '6px',
  padding: '3px 8px',
  borderRadius: '4px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontWeight: 600,
  alignSelf: 'flex-start',
});

const PlaceholderContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: '4px',
  opacity: 0.5,
});

const ContentArea = styled.div({
  flex: 1,
  overflow: 'auto',
});

const EmptyPlaceholder = styled(Typography)({
  color: 'rgba(0, 0, 0, 0.38)',
  fontStyle: 'italic',
});

// ─── Chart data helpers ──────────────────────────────────────────────────────

function resolveChartData(data: CanonicalWidgetData | null): ChartWidgetData | null {
  if (!data || data.type !== 'chart') return null;
  return data.data;
}

function extractSeriesKeys(chartData: ChartWidgetData): string[] {
  switch (chartData.type) {
    case 'bar':
    case 'stacked-bar':
      return chartData.chart.data.map(d => d.x);
    case 'pie':
      return chartData.chart.data.map(d => d.label);
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar': {
      const seen = new Set<string>();
      for (const row of chartData.chart.data) {
        for (const point of row.data) seen.add(point.x);
      }
      return Array.from(seen);
    }
    default:
      return [];
  }
}

function getDataColors(chartData: ChartWidgetData): string[] | undefined {
  switch (chartData.type) {
    case 'bar':
    case 'stacked-bar':
    case 'pie':
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return chartData.colors;
    default:
      return undefined;
  }
}

function resolveColors(chartData: ChartWidgetData, config: ChartWidgetConfig): string[] {
  const palette: string[] = config.colors ?? getDataColors(chartData) ?? DEFAULT_COLORS;
  const overrides = config.colorOverrides ?? {};
  const keys = extractSeriesKeys(chartData);
  if (keys.length === 0) return palette;
  return keys.map((key, i) => overrides[key] ?? palette[i % palette.length]);
}

// ─── Render functions ────────────────────────────────────────────────────────

function renderChart({ config, data }: WidgetRenderProps<'chart'>): React.ReactElement {
  const chartConfig = config.config;
  const chartData = resolveChartData(data);
  const colors = chartData ? resolveColors(chartData, chartConfig) : DEFAULT_COLORS;

  if (!chartData) {
    return (
      <PlaceholderContainer style={{ height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {chartConfig.title ?? 'Chart'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          No data available
        </Typography>
      </PlaceholderContainer>
    );
  }

  // For progress-bar and semi-circle widgets, strip the internal title so the
  // widget component doesn't render its own bold heading — the block-level
  // title shown above is sufficient.
  const finalChartData =
    chartData.type === 'progress-bar-widget' || chartData.type === 'semi-circle-progress-widget'
      ? { ...chartData, title: '' }
      : chartData;

  return (
    <WidgetContainer style={{ height: '100%' }}>
      {chartConfig.title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', pb: '4px' }}>
          {chartConfig.title}
        </Typography>
      )}
      <ChartVisualizationUI chartData={finalChartData} colors={colors} />
      {config.sourceAttribution?.label && <Attribution>{config.sourceAttribution.label}</Attribution>}
    </WidgetContainer>
  );
}

function renderMetricCard({ config, data }: WidgetRenderProps<'metric_card'>): React.ReactElement {
  const metricConfig = config.config;
  const { values, labels } = data
    ? { values: data.data.values, labels: data.data.labels }
    : { values: [] as string[], labels: [] as string[] };

  return (
    <WidgetContainer style={{ height: '100%' }}>
      <KPIWidget
        title={metricConfig.title}
        backgroundColor={metricConfig.backgroundColor ?? 'transparent'}
        values={values}
        labels={labels}
      />
      {config.sourceAttribution?.label && <Attribution>{config.sourceAttribution.label}</Attribution>}
    </WidgetContainer>
  );
}

/** Returns true when a ProseMirror doc node has no visible text content. */
function isEmptyDoc(node: RootNode): boolean {
  const doc = node as { content?: Array<{ content?: unknown[] }> };
  if (!doc.content || doc.content.length === 0) return true;
  return doc.content.every(child => !child.content || child.content.length === 0);
}

function renderText({ config, data }: WidgetRenderProps<'text'>): React.ReactElement {
  const textConfig = config.config;

  // Markdown branch
  if (textConfig.textMode === 'markdown') {
    const markdownContent = data?.data.content ?? '';
    return (
      <WidgetContainer style={{ height: '100%' }}>
        <ContentArea>
          {markdownContent.trim()
            ? <ReactMarkdown>{markdownContent}</ReactMarkdown>
            : <EmptyPlaceholder variant="body2">No content — edit this widget to add markdown text.</EmptyPlaceholder>
          }
        </ContentArea>
        {config.sourceAttribution?.label && <Attribution>{config.sourceAttribution.label}</Attribution>}
      </WidgetContainer>
    );
  }

  // Rich-text branch
  const content: RootNode | null = (() => {
    if (data?.data.content) {
      try { return JSON.parse(data.data.content) as RootNode; }
      catch { return null; }
    }
    return null;
  })();

  const isEmpty = content === null || isEmptyDoc(content);

  return (
    <WidgetContainer style={{ height: '100%' }}>
      <ContentArea>
        {isEmpty
          ? <EmptyPlaceholder variant="body2">No content — edit this widget to add rich text.</EmptyPlaceholder>
          : <RichTextUI content={content} />
        }
      </ContentArea>
    </WidgetContainer>
  );
}

// ─── Code widget ──────────────────────────────────────────────────────────────

const DEFAULT_CODE_WIDGET_JSX = `import { data } from './data';

export default function App() {
  return (
    <div className="p-4 flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-gray-800">My Widget</h2>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <pre className="text-xs text-gray-600 overflow-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    </div>
  );
}
`;

function renderCode({ config, data }: WidgetRenderProps<'code'>): React.ReactElement {
  return (
    <WidgetContainer style={{ height: '100%', padding: 0 }}>
      <React.Suspense fallback={null}>
        <CodeWidgetUI config={config.config} data={data?.data ?? null} />
      </React.Suspense>
    </WidgetContainer>
  );
}

// ─── Agent data examples per widget/chart type ───────────────────────────────

function getChartAgentDataExample(config: ChartWidgetConfig): CanonicalWidgetData {
  const chartType = config.chartType ?? 'bar';
  switch (chartType) {
    case 'pie':
      return {
        type: 'chart',
        data: {
          type: 'pie',
          chart: {
            title: 'Example Chart',
            xAxisTitle: '',
            yAxisTitle: '',
            data: [
              { id: 'cat_a', label: 'Category A', value: 42 },
              { id: 'cat_b', label: 'Category B', value: 28 },
              { id: 'cat_c', label: 'Category C', value: 30 },
            ],
          },
        },
      };
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return {
        type: 'chart',
        data: {
          type: chartType,
          chart: {
            title: 'Example Chart',
            xAxisTitle: 'Quarter',
            yAxisTitle: 'Value',
            indexBy: 'x',
            data: [
              { index: 'Series A', data: [{ x: 'Q1', y: 42 }, { x: 'Q2', y: 67 }] },
              { index: 'Series B', data: [{ x: 'Q1', y: 38 }, { x: 'Q2', y: 55 }] },
            ],
          },
        },
      };
    case 'multiple-line':
      return {
        type: 'chart',
        data: {
          type: 'multiple-line' as const,
          chart: {
            title: 'Example Chart',
            xAxisTitle: 'Quarter',
            yAxisTitle: 'Value',
            data: [
              { id: 'Series A', data: [{ x: 'Q1', y: 42 }, { x: 'Q2', y: 67 }] },
              { id: 'Series B', data: [{ x: 'Q1', y: 38 }, { x: 'Q2', y: 55 }] },
            ],
          },
        },
      };
    case 'kpi-widget':
    case 'progress-bar-widget':
    case 'semi-circle-progress-widget':
      return {
        type: 'chart',
        data: {
          type: chartType,
          title: 'KPI Overview',
          backgroundColor: '#ffffff',
          values: ['4,200', '87%', '$12.5K'],
          labels: ['Total Responses', 'Satisfaction Rate', 'Revenue'],
        },
      };
    case 'data-table':
      return {
        type: 'chart',
        data: {
          type: 'data-table',
          title: 'Example Table',
          headerColor: DEFAULT_COLORS[0],
          data: {
            columnNames: [['Category'], ['Value'], ['Change']],
            rowNames: [['1'], ['2'], ['3']],
            records: [
              ['Category A', '42', '+5%'],
              ['Category B', '67', '-2%'],
              ['Category C', '53', '+12%'],
            ],
          },
        },
      };
    // bar, stacked-bar (default)
    case 'line':
      return {
        type: 'chart',
        data: {
          type: 'line' as const,
          chart: {
            title: 'Example Chart',
            xAxisTitle: 'Category',
            yAxisTitle: 'Value',
            id: 'series',
            data: [
              { id: 'series', data: [{ x: 'Category A', y: 42 }, { x: 'Category B', y: 67 }, { x: 'Category C', y: 53 }] },
            ],
          },
        },
      };
    default:
      return {
        type: 'chart',
        data: {
          type: chartType,
          chart: {
            title: 'Example Chart',
            xAxisTitle: 'Category',
            yAxisTitle: 'Value',
            indexBy: 'x',
            data: [
              { x: 'Category A', y: 42 },
              { x: 'Category B', y: 67 },
              { x: 'Category C', y: 53 },
            ],
          },
        },
      };
  }
}

// ─── Rep Dashboard render + agent example ─────────────────────────────────────

function renderRepDashboard({ config, data }: WidgetRenderProps<'rep_dashboard'>): React.ReactElement {
  const repConfig = config.config;
  const repData = data?.data ?? null;

  if (!repData) {
    return (
      <PlaceholderContainer style={{ height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {repConfig.title ?? 'Rep Dashboard'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          No data available
        </Typography>
      </PlaceholderContainer>
    );
  }

  return (
    <div style={{ height: '100%', width: '100%', overflow: 'auto', padding: 8, boxSizing: 'border-box' }}>
      <RepDashboardWidget data={repData} config={repConfig} />
    </div>
  );
}

/**
 * Estimate the pixel height needed to render a rep dashboard with the given
 * data, then convert to row-span units (ROW_HEIGHT = 380 px per unit).
 */
function computeRepDashboardRowSpan(data: CanonicalWidgetData): number | undefined {
  if (data.type !== 'rep_dashboard') return undefined;
  const d = data.data;
  let px = 100; // banner + container gaps
  if (d.kpis.length > 0) {
    const kpiRows = Math.ceil(d.kpis.length / 5);
    px += 120 + kpiRows * 140;
  }
  if (d.opportunities.length > 0) {
    px += 100 + d.opportunities.length * 50 + (d.infoBoxText ? 80 : 0);
  }
  if (d.tableRows.length > 0) {
    const visibleRows = Math.min(5, d.tableRows.length);
    px += 150 + visibleRows * 55;
  }
  return Math.max(2, Math.ceil(px / 380));
}

function getRepDashboardAgentDataExample(): CanonicalWidgetData {
  return {
    type: 'rep_dashboard',
    data: {
      summaryText: 'Here is a quick 2-minute summary of your performance metrics.',
      kpis: [
        { label: 'Calls per Day', value: '9.14', unit: 'HCPs', change: '+5.09%', changeLabel: 'prior QTD', changeType: 'positive' },
        { label: 'FEA', value: '68.31', unit: '%', change: '0%', changeLabel: 'prior QTD', changeType: 'neutral' },
        { label: 'Reach', value: '27.00', unit: '%', change: '0%', changeLabel: 'prior QTD', changeType: 'neutral' },
      ],
      opportunities: [
        'Example opportunity insight text.',
      ],
      infoBoxText: 'Optional highlighted callout text.',
      tableRows: [
        { id: '12345678', name: 'Jane Doe', address: 'New York, NY', callGoal: 40, remainingCalls: 12, profileId: '12345678' },
      ],
    },
  };
}

// ─── Factory function ─────────────────────────────────────────────────────────

/**
 * createDefaultRegistry — creates a new WidgetRegistry pre-loaded with the
 * three built-in widget types: chart, text, metric_card.
 *
 * Each entry uses a `render` function that maps (config, data) to the shared
 * Widgets/ components — no intermediate wrapper components needed.
 *
 * Apps can extend the result with custom widget types via `registry.register()`.
 */
export function createDefaultRegistry(): WidgetRegistry {
  const registry = new WidgetRegistry();

  registry.register({
    type: 'chart',
    label: 'Chart',
    description: 'Bar, stacked bar, pie, and other chart types',
    category: 'visualization',
    render: renderChart,
    configSchema: ChartWidgetConfigSchema,
    defaultConfig: { title: '', chartType: 'bar' },
    getAgentDataExample: getChartAgentDataExample,
    minW: 1,
    minH: 2,
  });

  registry.register({
    type: 'text',
    label: 'Text',
    description: 'Rich text block with formatting',
    category: 'content',
    render: renderText,
    configSchema: TextWidgetConfigSchema,
    defaultConfig: { title: '' },
    getAgentDataExample: () => ({
      type: 'text',
      data: {
        content: '<p>Your HTML or plain text content here.</p>',
      },
    }),
    minW: 1,
    minH: 1,
  });

  registry.register({
    type: 'metric_card',
    label: 'Metric Card',
    description: 'KPI tiles showing key numbers at a glance',
    category: 'metric',
    render: renderMetricCard,
    configSchema: MetricCardWidgetConfigSchema,
    defaultConfig: { title: '' },
    getAgentDataExample: () => ({
      type: 'metric_card',
      data: {
        values: ['4,200', '87%', '$12.5K'],
        labels: ['Total Responses', 'Satisfaction Rate', 'Revenue'],
      },
    }),
    minW: 1,
    minH: 1,
  });

  registry.register({
    type: 'code',
    label: 'Code',
    description: 'Custom React component with Tailwind CSS and optional data injection',
    category: 'custom',
    render: renderCode,
    configSchema: CodeWidgetConfigSchema,
    defaultConfig: { title: '', jsx: DEFAULT_CODE_WIDGET_JSX },
    getAgentDataExample: () => ({
      type: 'code',
      data: { items: [], total: 0 },
    }),
    minW: 1,
    minH: 2,
  });

  registry.register({
    type: 'rep_dashboard',
    label: 'Rep Dashboard',
    description: 'Multi-section sales rep performance dashboard with KPIs, opportunities, and HCP table',
    category: 'custom',
    render: renderRepDashboard,
    configSchema: RepDashboardWidgetConfigSchema,
    defaultConfig: { title: 'Rep Performance Dashboard' },
    getAgentDataExample: getRepDashboardAgentDataExample,
    autoRowSpan: computeRepDashboardRowSpan,
    minW: 3,
    minH: 3,
    noBorder: true,
  });

  registry.register({
    type: 'quote',
    label: 'Quote',
    description: 'Quote card with optional speaker attribution and media reference',
    category: 'content',
    render: renderQuote,
    configSchema: QuoteWidgetConfigSchema,
    defaultConfig: { title: '' },
    getAgentDataExample: () => ({
      type: 'quote' as const,
      data: {
        content: 'This is an example quote from a research participant.',
        speaker: 'Participant A',
      },
    }),
    minW: 1,
    minH: 1,
  });

  registry.register({
    type: 'segment_overview',
    label: 'Segment Overview',
    description: 'Segment narrative with commercial action and data gaps',
    category: 'content',
    render: renderSegmentOverview,
    configSchema: z.object({ title: z.string().optional() }),
    defaultConfig: { title: 'Segment Overview' },
    hidden: true,
    noBorder: true,
    minW: 3,
    minH: 2,
  });

  return registry;
}

// ─── Quote widget ───────────────────────────────────────────────────────────

function renderQuote({ config, data }: WidgetRenderProps<DashboardWidgetType>): React.ReactElement {
  const quoteConfig = config.config as { title?: string; speaker?: string };
  const quoteData = data?.data as { content?: string; speaker?: string } | null;

  const content = quoteData?.content ?? '';
  const speaker = quoteData?.speaker ?? quoteConfig.speaker ?? '';

  if (!content) {
    return (
      <PlaceholderContainer style={{ height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {quoteConfig.title ?? 'Quote'}
        </Typography>
        <Typography variant="caption" color="text.disabled">No quote content</Typography>
      </PlaceholderContainer>
    );
  }

  return (
    <WidgetContainer style={{ height: '100%' }}>
      <Typography
        variant="body1"
        sx={{ fontStyle: 'italic', lineHeight: 1.6, color: 'text.secondary', borderLeft: '3px solid #5C73AD', pl: 2 }}
      >
        &ldquo;{content}&rdquo;
      </Typography>
      {speaker && (
        <Typography variant="caption" sx={{ mt: 1, color: 'text.disabled' }}>
          &mdash; {speaker}
        </Typography>
      )}
    </WidgetContainer>
  );
}

// ─── Segment Overview widget ────────────────────────────────────────────────

function renderSegmentOverview({ config, data }: WidgetRenderProps<DashboardWidgetType>): React.ReactElement {
  const overviewConfig = config.config as { title?: string };
  const overviewData = data?.data as {
    narrative?: string;
    commercialAction?: string;
    gapAnalysis?: string | Record<string, unknown>;
  } | null;

  if (!overviewData) {
    return (
      <PlaceholderContainer style={{ height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {overviewConfig.title ?? 'Segment Overview'}
        </Typography>
        <Typography variant="caption" color="text.disabled">No data</Typography>
      </PlaceholderContainer>
    );
  }

  return (
    <WidgetContainer style={{ height: '100%' }}>
      {overviewData.narrative && (
        <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary', mb: 2 }}>
          {overviewData.narrative}
        </Typography>
      )}
      {(overviewData.commercialAction || overviewData.gapAnalysis) && (
        <div style={{ display: 'flex', gap: 16 }}>
          {overviewData.commercialAction && (
            <div style={{ flex: 1, borderRadius: 8, padding: 14, background: '#e3f9e8', border: '1px solid #4CB847', fontSize: 13, lineHeight: 1.5, color: '#4c4c4c' }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 6, color: '#2d7a2e' }}>Commercial Action</div>
              {overviewData.commercialAction}
            </div>
          )}
          {overviewData.gapAnalysis && (
            <div style={{ flex: 1, borderRadius: 8, padding: 14, background: '#fffbeb', border: '1px solid #f59e0b', fontSize: 13, lineHeight: 1.5, color: '#4c4c4c' }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 6, color: '#92400e' }}>Data Gaps</div>
              {typeof overviewData.gapAnalysis === 'string'
                ? overviewData.gapAnalysis
                : Object.entries(overviewData.gapAnalysis).map(([k, v]) => (
                    <div key={k}><strong style={{ textTransform: 'capitalize' }}>{k}:</strong> {String(v)}</div>
                  ))
              }
            </div>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}
